/* # LexwareService - Version 6.4
1. URL UPDATE: Umstellung auf die offizielle https://api.lexware.io/v1 Domain wie vom Nutzer gewünscht.
2. PROXY REFINEMENT: Optimierte Proxy-Kette für stabilere Verbindungen zur neuen Domain.
3. DATE HANDLING: Sicherstellung, dass API-Calls das korrekte ISO-Format nutzen, während die UI de-DE anzeigt.
*/
class LexwareService {
  constructor() {
    // Umstellung auf die neue offizielle Domain
    this.baseURL = 'https://api.lexware.io/v1';
    this.apiKey = null;
    
    this.proxies = [
      {
        name: 'CORSProxy.io',
        build: (target) => `https://corsproxy.io/?${encodeURIComponent(target)}`
      },
      {
        name: 'ThingProxy',
        build: (target) => `https://thingproxy.freeboard.io/fetch/${target}`
      },
      {
        name: 'AllOrigins',
        build: (target) => `https://api.allorigins.win/raw?url=${encodeURIComponent(target)}`
      }
    ];
  }

  initialize(apiKey) {
    this.apiKey = apiKey;
  }

  getHeaders() {
    return {
      'Authorization': `Bearer ${this.apiKey}`,
      'Accept': 'application/json',
      'Content-Type': 'application/json'
    };
  }

  async fetchInvoices(fromDate = '2026-01-01', status = 'any', types = 'invoice', onLog = null) {
    if (!this.apiKey) throw new Error('API Key fehlt');
    
    const queryParams = [
      `voucherType=${types}`,
      `voucherStatus=${status}`,
      `voucherDateFrom=${fromDate}`,
      `page=0`,
      `size=100`
    ].join('&');

    const targetUrl = `${this.baseURL}/voucherlist?${queryParams}`;
    let lastError = null;

    for (const proxy of this.proxies) {
      const url = proxy.build(targetUrl);
      if (onLog) onLog(`Versuche Verbindung über ${proxy.name} zu api.lexware.io...`);
      
      try {
        const response = await fetch(url, {
          method: 'GET',
          headers: this.getHeaders()
        });

        if (!response.ok) {
          if (response.status === 401) throw new Error('API-Key ungültig (Unauthorized).');
          const errorText = await response.text();
          throw new Error(`HTTP ${response.status}: ${errorText.substring(0, 100)}`);
        }

        const data = await response.json();
        const vouchers = data.content || data.vouchers || (Array.isArray(data) ? data : []);
        
        if (onLog) onLog(`Erfolg! ${vouchers.length} Belege von Lexware geladen.`);

        return vouchers.map(v => {
          const brutto = v.totalAmount || v.totalGrossAmount || 0;
          const steuer = v.taxAmount || v.totalTaxAmount || 0;
          const netto = v.netAmount || (brutto - steuer);

          return {
            id: v.id || Math.random().toString(36).substr(2, 9),
            'RG-Datum': v.voucherDate || v.issueDate,
            'RG-Nr.': v.voucherNumber || v.number || 'N/A',
            'Gesamtbetrag': brutto,
            'Steuerbetrag': steuer,
            'Kundenummer': v.contactId || v.customerNumber || 'N/A',
            'Firmenname': v.contactName || v.customerName || 'Unbekannt',
            'Währung': v.currency || 'EUR',
            'Status': v.voucherStatus || 'N/A',
            _netto: netto,
            _isApiData: true
          };
        });

      } catch (error) {
        lastError = error.message;
        if (onLog) onLog(`${proxy.name} fehlgeschlagen: ${error.message}`, 'warn');
        if (error.message.includes('API-Key')) throw error;
      }
    }

    throw new Error(`Synchronisation fehlgeschlagen. Bitte API-Key und Internetverbindung prüfen.`);
  }

  async testConnection() {
    try {
      await this.fetchInvoices('2026-03-01', 'any', 'invoice');
      return true;
    } catch (e) {
      return false;
    }
  }
}

export const lexwareService = new LexwareService();